import { EditOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import {
  Card as AntdCard,
  Badge,
  Button,
  Col,
  Collapse,
  Descriptions,
  Divider,
  Drawer,
  Progress,
  Row,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import { FormInstance } from "antd/lib/form";
import Text from "antd/lib/typography/Text";
import { AxiosResponse } from "axios";
import moment from "moment";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import AttachmentsSection, {
  UNASSIGN_ATTACHMENT_FROM_REDEMPTION_REQUEST,
} from "../components/AttachmentsSection";
import Card from "../components/Card";
import CardSection from "../components/CardSection";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import NotesSection from "../components/NotesSection";
import { PaymentAccountCard } from "../components/PaymentsAccountsView";
import entitlements from "../config/entitlements";
import {
  ASSIGN_ATTACHMENT_TO_REDEMPTION_REQUEST,
  ASSIGN_FOLLOWERS_TO_REDEMPTIONS_REQUEST,
  ASSIGN_NOTE_TO_REDEMPTION_REQUEST,
  CHECK_DEALS,
  CREATE_NOTE_TICKET,
  GET_REDEMPTION_REQUEST,
  GET_REDEMPTION_REQUEST_LOGS,
  GET_REDEMPTION_REQUEST_REASONS,
  GET_REQUEST_NOTIFIER_RECORDS_QUERY,
  MOVE_REDEMPTION_REQUEST_TO_STATE,
} from "../gql/redemption-request";
import {
  ActiveDrawerContent,
  APIError,
  Attachment,
  Changelog,
  Deal,
  Entitlement,
  FormFieldConfig,
  MultipleChangelog,
  Note,
  NotificationRecord,
  CustomerTransaction,
  RedemptionRequestAllocation,
  RedemptionRequestAttachment,
  RedemptionsRequest,
  RequestReason,
  SelectOptionsInterface,
  Ticket,
  TransferRequest,
  TransitionToNextState,
  User,
} from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  checkPermission,
  formatRedemptionPurpose,
  formatSnakeCase,
  formatter,
  generateStateButtonText,
  generateDisplayErrorMessage,
  generateNameFromCustomer,
  handleValidationErrors,
  redemptionTypes,
  request,
  responseHandler,
  spaceCamel,
  dealInsertValidDates,
  getStateColor,
} from "../helpers/utilities";
import { CREATE_ATTACHMENT } from "../gql/general";
import { CREATE_NOTE } from "../gql/notes";
import { ALL_USERS_QUERY } from "../gql/users";
import { tabPaneStyle } from "../helpers/constants";
import {
  notificationColumns,
  NotificationRecordTableFactory,
} from "./NotificationRecords";
import RequestChangeLogs from "../components/RequestChangeLogs";

const { Panel } = Collapse;

interface State {
  id: string | number;
  isLoading: boolean;
  showReasonModal: boolean;
  isReasonLoading: boolean;
  showConfirmActionModal: boolean;
  isConfirmActionLoading: boolean;
  users: User[];
  selectedTransitionState?: string;
  redemptionRequest?: RedemptionsRequest;
  notes: Note[];
  selectedRedemptionNoteId: string;
  currentNote?: Note;
  attachments: Attachment[];
  showTicketForm: boolean;
  requestLogs: MultipleChangelog | Changelog[];
  isTicketCreateLoading: boolean;
  showDrawer: boolean;
  redemptionAllocationAmount?: RedemptionRequestAllocation;
  redemptionRequestReasons: RequestReason[];
  redemptionRequestAttachments: RedemptionRequestAttachment[];
  attachmentIds: string[];
  disableReasonButton: boolean;
  currentRequestsToProcess: number;
  availableTransitionStates?: TransitionToNextState[];
  activeDrawerContent: ActiveDrawerContent;
  showCancelModal: boolean;
  notifications?: NotificationRecord[];
  notifyCustomer: boolean;
  notifyClient: boolean;
  vestingRuleUrl?: string;
}

interface RouteParams {
  id: string;
}

interface Props
  extends RouteComponentProps<
    RouteParams,
    any,
    { type: string; requests?: RedemptionsRequest[] }
  > {
  entitlement: Entitlement;
  user: User;
}

const contributionTypeLabels: Record<string, string> = {
  employer_post_tax: "Employer Post-Tax",
  employee_post_tax: "Employee Post-Tax",
  employer_pre_tax: "Employer Pre-Tax",
  employee_pre_tax: "Employee Pre-Tax",
  contribution: "Contribution",
};

const getColorForContributionType = (type: string): string => {
  const colorMap: Record<string, string> = {
    employee_pre_tax: "#1890ff",
    employee_post_tax: "#52c41a",
    employer_pre_tax: "#722ed1",
    employer_post_tax: "#fa8c16",
    contribution: "#13c2c2",
  };
  return colorMap[type] || "#d9d9d9";
};

export function RedemptionAllocationBreakdown(props: {
  allocation: RedemptionRequestAllocation;
  redemptionRequest?: RedemptionsRequest | CustomerTransaction;
}): JSX.Element {
  const {
    amount,
    customer_contributions_deductions: deductions,
    customer_contributions_units_deductions: unitsDeductions,
    previous_customer_contributions: previousContributions,
    vested_percentage: vestedPercentage,
  } = props.allocation;

  const { redemptionRequest } = props;

  const formatAmount = (value: number | undefined): string => {
    if (value === undefined || value === null) return "0.00";
    return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const formatUnits = (value: number | undefined): string => {
    if (value === undefined || value === null) return "0.00";
    return parseFloat(value.toString()).toFixed(2);
  };

  const prepareTableData = (): any[] => {
    const rows: any[] = [];

    // Map contribution types to their final field names on redemptionRequest
    const contributionTypeConfig = [
      { type: "employer_post_tax", finalField: "finalEmployerPostTax" },
      { type: "employer_pre_tax", finalField: "finalEmployerPreTax" },
      { type: "employee_post_tax", finalField: "finalEmployeePostTax" },
      { type: "employee_pre_tax", finalField: "finalEmployeePreTax" },
      { type: "contribution", finalField: "finalContribution" },
    ];

    let totalPrevious = 0;
    let totalDeducted = 0;
    let totalUnits = 0;

    contributionTypeConfig.forEach(({ type, finalField }) => {
      const previousBalance = (previousContributions as any)?.[type] ?? 0;
      // Use final amount from redemptionRequest if available, otherwise fall back to allocation breakdown
      const amountDeducted =
        (redemptionRequest as any)?.[finalField] ??
        (deductions as any)?.[type] ??
        0;
      const unitsDeducted = (unitsDeductions as any)?.[type] ?? 0;

      totalPrevious += parseFloat(previousBalance) || 0;
      totalDeducted += parseFloat(amountDeducted) || 0;
      totalUnits += parseFloat(unitsDeducted) || 0;

      rows.push({
        key: type,
        type,
        displayName: contributionTypeLabels[type] || type,
        previousBalance: parseFloat(previousBalance) || 0,
        amountDeducted: parseFloat(amountDeducted) || 0,
        unitsDeducted: parseFloat(unitsDeducted) || 0,
        isTotal: false,
      });
    });

    if (rows.length > 0) {
      rows.push({
        key: "totals",
        type: "totals",
        displayName: "Totals",
        previousBalance: totalPrevious,
        amountDeducted:
          (redemptionRequest as any)?.finalAllocationAmount ?? totalDeducted,
        unitsDeducted:
          (redemptionRequest as any)?.redemptionUnits ?? totalUnits,
        isTotal: true,
      });
    }

    return rows;
  };

  const columns = [
    {
      title: "Contribution Type",
      dataIndex: "displayName",
      key: "displayName",
      render: (text: string, record: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!record.isTotal && (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: getColorForContributionType(record.type),
                display: "inline-block",
              }}
            />
          )}
          <span style={{ fontWeight: record.isTotal ? 600 : 500 }}>{text}</span>
        </div>
      ),
    },
    {
      title: "Previous Balance (GHS)",
      dataIndex: "previousBalance",
      key: "previousBalance",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span style={{ fontWeight: record.isTotal ? 600 : 400 }}>
          {formatAmount(value)}
        </span>
      ),
    },
    {
      title: "Amount Deducted (GHS)",
      dataIndex: "amountDeducted",
      key: "amountDeducted",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span
          style={{
            color: value > 0 ? "#ff4d4f" : undefined,
            fontWeight: record.isTotal ? 600 : 400,
          }}
        >
          {formatAmount(value)}
        </span>
      ),
    },
    {
      title: "Units Deducted",
      dataIndex: "unitsDeducted",
      key: "unitsDeducted",
      align: "right" as const,
      render: (value: number, record: any) => (
        <span style={{ fontWeight: record.isTotal ? 600 : 400 }}>
          {formatUnits(value)}
        </span>
      ),
    },
  ];

  const tableData = prepareTableData();
  const totalsRow = tableData.find((row) => row.isTotal);

  const r = redemptionRequest as any;
  const redemptionAmount =
    r?.redemptionAmount ?? parseFloat(amount?.toString() ?? "0");
  const totalAllocation =
    r?.finalAllocationAmount ?? totalsRow?.amountDeducted ?? redemptionAmount;
  const withholdingTax = redemptionRequest?.withholdingTax ?? 0;
  const platformFee = redemptionRequest?.platformFee ?? 0;
  const amountPaid =
    r?.amountPaid ?? redemptionAmount - withholdingTax - platformFee;
  const valueDate = r?.valueDate ?? r?.priceDate;
  // Handle various formats the vested percentage might come in
  const getVestedPercentage = (): number | null => {
    if (vestedPercentage === null || vestedPercentage === undefined)
      return null;
    // Check if it's directly a number
    if (typeof vestedPercentage === "number") {
      return vestedPercentage;
    }
    // Check object properties
    if (vestedPercentage.parsedValue !== undefined) {
      return parseFloat(String(vestedPercentage.parsedValue));
    }
    if (vestedPercentage.parsed_value !== undefined) {
      return parseFloat(String(vestedPercentage.parsed_value));
    }
    if (vestedPercentage.source !== undefined) {
      return parseFloat(String(vestedPercentage.source));
    }
    return null;
  };
  const vestedPct = getVestedPercentage();
  const totalUnitsDeducted =
    r?.redemptionUnits ?? totalsRow?.unitsDeducted ?? 0;
  const waiveOffTax = r?.waiveOffTax ?? false;

  return (
    <div style={{ padding: "16px 0" }}>
      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <AntdCard
            style={{
              borderLeft: "4px solid #1d3764",
              borderRadius: 8,
            }}
            bodyStyle={{ padding: 16 }}
          >
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
              Redemption Amount
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Statistic
                value={redemptionAmount}
                precision={2}
                prefix={
                  <span style={{ fontSize: 14, color: "#8c8c8c" }}>GHS</span>
                }
                valueStyle={{ fontSize: 24, fontWeight: 600 }}
              />
              {r?.totalAmountType && (
                <Tag
                  color={r.totalAmountType === "net" ? "green" : "blue"}
                  style={{ textTransform: "capitalize" }}
                >
                  {r.totalAmountType}
                </Tag>
              )}
            </div>
          </AntdCard>
        </Col>
        <Col span={6}>
          <AntdCard
            style={{
              borderLeft: "4px solid #1d3764",
              borderRadius: 8,
            }}
            bodyStyle={{ padding: 16 }}
          >
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
              Total Allocation
            </div>
            <Statistic
              value={totalAllocation}
              precision={2}
              prefix={
                <span style={{ fontSize: 14, color: "#8c8c8c" }}>GHS</span>
              }
              valueStyle={{ fontSize: 24, fontWeight: 600 }}
            />
          </AntdCard>
        </Col>
        <Col span={6}>
          <AntdCard
            style={{
              borderLeft: "4px solid #fa8c16",
              borderRadius: 8,
            }}
            bodyStyle={{ padding: 16 }}
          >
            <div style={{ fontSize: 12, color: "#fa8c16", marginBottom: 4 }}>
              Amount Paid (Customer Receives)
            </div>
            <Statistic
              value={amountPaid}
              precision={2}
              prefix={
                <span style={{ fontSize: 14, color: "#8c8c8c" }}>GHS</span>
              }
              valueStyle={{ fontSize: 24, fontWeight: 600, color: "#fa8c16" }}
            />
          </AntdCard>
        </Col>
        <Col span={6}>
          <AntdCard
            style={{
              borderLeft: "4px solid #722ed1",
              borderRadius: 8,
            }}
            bodyStyle={{ padding: 16 }}
          >
            <div style={{ fontSize: 12, color: "#722ed1", marginBottom: 4 }}>
              Withholding Tax
            </div>
            {waiveOffTax && (
              <Tag color="green" style={{ marginLeft: 8 }}>
                Tax Waived
              </Tag>
            )}
            <Statistic
              value={withholdingTax}
              precision={2}
              prefix={
                <span style={{ fontSize: 14, color: "#8c8c8c" }}>GHS</span>
              }
              valueStyle={{ fontSize: 24, fontWeight: 600, color: "#722ed1" }}
            />
          </AntdCard>
        </Col>
      </Row>

      {/* Allocation Table */}
      {tableData.length > 0 && (
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          rowKey="key"
          bordered
          style={{ marginBottom: 24 }}
          rowClassName={(record) => (record.isTotal ? "totals-row" : "")}
        />
      )}

      {/* Footer Info */}
      <Row
        gutter={24}
        style={{
          padding: "16px",
          background: "#f5f5f5",
          borderRadius: 8,
          marginBottom: 24,
        }}
      >
        <Col span={8}>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>Platform Fee</div>
          <div style={{ fontWeight: 600, marginTop: 4 }}>
            {platformFee > 0 ? `GHS ${formatAmount(platformFee)}` : "—"}
          </div>
        </Col>
        <Col span={8}>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            Vested Percentage
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 4,
            }}
          >
            {vestedPct !== null ? (
              <>
                <Progress
                  percent={vestedPct}
                  size="small"
                  style={{ width: 80, margin: 0 }}
                  showInfo={false}
                  strokeColor="#52c41a"
                />
                <span style={{ fontWeight: 600 }}>{vestedPct}%</span>
              </>
            ) : (
              <span style={{ fontWeight: 600 }}>—</span>
            )}
          </div>
        </Col>
      </Row>

      {/* Bottom Info Bar */}
      <Row
        justify="space-between"
        align="middle"
        style={{
          padding: "12px 16px",
          background: "#fafafa",
          borderRadius: 8,
          border: "1px solid #f0f0f0",
        }}
      >
        <Col>
          <span style={{ color: "#8c8c8c", marginRight: 8 }}>Value Date:</span>
          <span style={{ fontWeight: 500 }}>
            {valueDate
              ? moment(valueDate, "YYYY-MM-DD HH:mm:ss").format("MMM DD, YYYY")
              : "N/A"}
          </span>
        </Col>
        <Col>
          <span style={{ color: "#8c8c8c", marginRight: 8 }}>
            Total Redemption Units:
          </span>
          <span style={{ fontWeight: 500 }}>
            {formatUnits(totalUnitsDeducted)}
          </span>
        </Col>
      </Row>
    </div>
  );
}

export function RedemptionAllocationCard(props: {
  allocation: RedemptionRequestAllocation;
}): JSX.Element {
  const {
    amount,
    customer_contributions_deductions: deduction,
    previous_customer_contributions: previousContribution,
  } = props.allocation;
  return (
    <div>
      {amount && (
        <CardSection
          headingText="Amount"
          data={props.allocation.amount?.toString() ?? ""}
        />
      )}
      {deduction && (
        <>
          <hr className="hr-light" />
          <h4 style={{ fontWeight: "bolder" }}>
            Customer Contribution Deduction
          </h4>
        </>
      )}
      {deduction?.hasOwnProperty("employee_post_tax") && (
        <CardSection
          headingText="Employee Post Tax Contribution"
          data={formatter.format(deduction.employee_post_tax ?? 0)}
        />
      )}
      {deduction?.hasOwnProperty("employee_pre_tax") && (
        <CardSection
          headingText="Employee Pre Tax Contribution"
          data={formatter.format(deduction.employee_pre_tax ?? 0)}
        />
      )}
      {deduction?.hasOwnProperty("employer_post_tax") && (
        <CardSection
          headingText="Employer Post Tax Contribution"
          data={formatter.format(deduction.employer_post_tax ?? 0)}
        />
      )}
      {deduction?.hasOwnProperty("employer_pre_tax") && (
        <CardSection
          headingText="Employer Pre Tax Contribution"
          data={formatter.format(deduction.employer_pre_tax ?? 0)}
        />
      )}
      {deduction?.hasOwnProperty("contribution") && (
        <CardSection
          headingText="Contribution"
          data={formatter.format(deduction.contribution ?? 0)}
        />
      )}
      {previousContribution && (
        <>
          <hr className="hr-light" />
          <h4 style={{ fontWeight: "bolder" }}>
            Previous Customer Contribution{" "}
          </h4>
        </>
      )}
      {previousContribution?.hasOwnProperty("employee_post_tax") && (
        <CardSection
          headingText="Employee Post Tax Contribution"
          data={formatter.format(previousContribution?.employee_post_tax ?? 0)}
        />
      )}
      {previousContribution?.hasOwnProperty("employee_pre_tax") && (
        <CardSection
          headingText="Employee Pre Tax Contribution"
          data={formatter.format(previousContribution?.employee_pre_tax ?? 0)}
        />
      )}
      {previousContribution?.hasOwnProperty("employer_post_tax") && (
        <CardSection
          headingText="Employer Post Tax Contribution"
          data={formatter.format(previousContribution?.employer_post_tax ?? 0)}
        />
      )}
      {previousContribution?.hasOwnProperty("employer_pre_tax") && (
        <CardSection
          headingText="Employer Pre Tax Contribution"
          data={formatter.format(previousContribution?.employer_pre_tax ?? 0)}
        />
      )}
      {previousContribution?.hasOwnProperty("contribution") && (
        <CardSection
          headingText="Contribution"
          data={formatter.format(previousContribution?.contribution ?? 0)}
        />
      )}
    </div>
  );
}

export function FilePath(props: {
  redemptionRequest: RedemptionsRequest;
}): JSX.Element {
  const additionalData: Record<string, string> | undefined =
    props?.redemptionRequest.additionalData;
  const filePath = additionalData
    ? Object.entries(additionalData).filter(
        (data) =>
          data[0] === "folder_path" || data[0] === "vesting_folder_path",
      )
    : [];

  return (
    <Col span={24} style={{ marginTop: "5px" }}>
      {filePath.map(([key, value]) =>
        value ? (
          <Row key={key}>{`${capitalizeFirstLetter(
            formatSnakeCase(key),
          )}: ${value}`}</Row>
        ) : (
          <Row key={key}>{`${capitalizeFirstLetter(
            formatSnakeCase(key),
          )}: N/A`}</Row>
        ),
      )}
    </Col>
  );
}

const DealInfoContent: React.FC<{ deal: Deal; title: string }> = ({
  deal,
  title,
}) => (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Descriptions
          column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
          size="small"
        >
          <Descriptions.Item label="Deal Type">{deal.dealType}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={deal.status === "Completed" ? "green" : "orange"}>
              {deal.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Description">{deal.dealDescription}</Descriptions.Item>
          <Descriptions.Item label="Transaction Type">{deal.transactionType}</Descriptions.Item>
          <Descriptions.Item label="Total Amount">
            <Text strong>
              {formatter.format(deal.totalAmount || 0)}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Total Units">
            <Text strong>
              {deal.totalUnits?.toFixed(4) || "0.0000"}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Price Date">
            {deal.priceDate
              ? moment(deal.priceDate).format("YYYY-MM-DD")
              : "N/A"}
          </Descriptions.Item>
        </Descriptions>

        {deal.contributions && deal.contributions.length > 0 && (
          <>
            <Divider orientation="left">
              <Text strong>{title} Contributions</Text>
            </Divider>
            <Table
              dataSource={deal.contributions
                .map((contribution: any) => {
                  const amount = contribution.amount || 0;
                  const units = contribution.units || 0;
                  return {
                    ...contribution,
                    type: spaceCamel(contribution.deal_contribution_type || ""),
                    amount,
                    units,
                  };
                })
                .filter((c: any) => c.amount > 0 || c.units > 0)}
              pagination={false}
              size="small"
              rowKey={(record: any) => record.id || record.type}
            >
              <Table.Column
                title="Type"
                dataIndex="type"
                key="type"
                render={(text) => <Text strong>{formatSnakeCase(text)}</Text>}
              />
              <Table.Column
                title="Amount (GHS)"
                dataIndex="amount"
                key="amount"
                align="right"
                render={(value) => formatter.format(value || 0)}
              />
              <Table.Column
                title="Units"
                dataIndex="units"
                key="units"
                align="right"
                render={(value) => value?.toFixed(4) || "0.0000"}
              />
            </Table>
          </>
        )}
      </Col>
    </Row>
);

export const requestChangelogColumns = [
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "date",
    width: "25%",
    render(text: string): JSX.Element {
      const [date, time] = text.split(" ");
      return (
        <span>
          {moment(date, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD")} {time}
        </span>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "newValue",
    key: "status",
    align: "left" as const,
    render(text: string): JSX.Element {
      return (
        <Tag
          color={getStateColor(text)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(text)}
        </Tag>
      );
    },
  },
  {
    title: "Comment",
    dataIndex: "comment",
    key: "comment",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "User",
    dataIndex: "user",
    key: "user",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
];

export const RequestReasonUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  reasons: SelectOptionsInterface[];
  redemptionInfo?: RedemptionsRequest | undefined;
  isLoading?: boolean;
  selectedTransitionState?: string;
  handleReasonSelect?: (reason: string) => void;
  handleFileUploadChange?: (field: string, combinedInfo: string) => void;
  commentRequired?: boolean;
  availableTransitionStates?: TransitionToNextState[];
  type: "redemption" | "transaction" | "transfer";
  request?: TransferRequest;
  notifyCustomer?: boolean;
  notifyClient?: boolean;
  changeNotifyCustomer?: (checkedValue: boolean) => void;
  changeNotifyClient?: (checkedValue: boolean) => void;
}): JSX.Element => {
  const stringifiedUser = localStorage.getItem("user");
  const user = JSON.parse(stringifiedUser as string);

  const { canAccess } = checkPermission(
    user,
    entitlements.RedemptionRequests.deal.insertion,
  );

  let fieldsToHide: string[] = [];

  if (props.type !== "redemption") {
    fieldsToHide.push("notifyCustomer", "notifyClient");
  }

  const currentTransition = props.availableTransitionStates?.find(
    (transition) => transition.state === props.selectedTransitionState,
  );

  if (props.request?.toProduct?.productTier.name === "tier_2") {
    fieldsToHide.push(
      "firstContributionDate",
      "proofOfContributionDocumentUrl",
    );
  }

  if (currentTransition) {
    const fieldMapping = [
      {
        requirement: currentTransition.reason_required,
        field: "redemptionRequestReasonId",
      },
      {
        requirement: currentTransition.amount_paid_required,
        field: `${props.type === "redemption" ? "amountPaid" : "amount"}`,
      },
      {
        requirement: currentTransition.paid_date_required,
        field: `${props.type === "redemption" ? "paidDate" : "date"}`,
      },
      {
        requirement: currentTransition.value_date_required,
        field: "valueDate",
      },
      {
        requirement: currentTransition.correct_value_date_required,
        field: "correctValueDate",
      },
      {
        requirement: currentTransition.first_contribution_date_required,
        field: "firstContributionDate",
      },
      {
        requirement:
          currentTransition.proof_of_contribution_document_url_required,
        field: "proofOfContributionDocumentUrl",
      },
      {
        requirement: currentTransition.vesting_value_required,
        field: "vestingValue",
      },
      {
        requirement: currentTransition.vesting_rule_url_required,
        field: "vestingRuleUrl",
      },
    ];

    fieldMapping.forEach(({ requirement, field }) => {
      if (!requirement) {
        fieldsToHide.push(field);
      }
    });
  }

  // if (!canAccess) {
  //   fieldsToHide.push("valueDate");
  // }

  const fields: FormFieldConfig[] = [
    {
      id:
        props.type === "transfer"
          ? "transferRequestReasonId"
          : "redemptionRequestReasonId",
      name:
        props.type === "transfer"
          ? "transferRequestReasonId"
          : "redemptionRequestReasonId",
      rule: "string",
      inputType: "select",
      required: false,
      label: "Select Reason",
      placeholder: "Select Reason",
      selectHandler: (reason) => props.handleReasonSelect?.(reason),
      selectOptions: props.reasons,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "comment",
      name: "comment",
      required: !!props.commentRequired,
      inputType: "textArea",
      rule: "string",
      label: "Add Comment",
      placeholder: "Enter description",
      colConfig: {
        span: 24,
      },
    },
    {
      id: "valueDate",
      name: "valueDate",
      inputType: "defaultDate",
      required: false,
      rule: "object",
      label: "Value Date",
      elementStyle: { width: "100%" },
      disabledDate: dealInsertValidDates(canAccess),
      colConfig: {
        span: 24,
      },
    },
    {
      id: `${props.type === "redemption" ? "amountPaid" : "amount"}`,
      name: `${props.type === "redemption" ? "amountPaid" : "amount"}`,
      required: false,
      inputType: "number",
      rule: "number",
      label: props.type === "redemption" ? "Amount Paid" : "Transaction Amount",
      placeholder: "Enter amount",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
    {
      id: `${props.type === "redemption" ? "paidDate" : "date"}`,
      name: `${props.type === "redemption" ? "paidDate" : "date"}`,
      inputType: "defaultDate",
      required: true,
      rule: "object",
      label: props.type === "redemption" ? "Paid Date" : "Transaction Date",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "correctValueDate",
      name: "correctValueDate",
      inputType: "defaultDate",
      required: false,
      rule: "object",
      label: "Correct Value Date",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "firstContributionDate",
      name: "firstContributionDate",
      inputType: "defaultDate",
      required: true,
      rule: "object",
      label: "First Contribution Date",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "proofOfContributionDocumentUrl",
      name: "proofOfContributionDocumentUrl",
      label: "Proof of Contribution",
      inputType: "fileUpload",
      placeholder: "Proof of Contribution",
      rule: "string",
      changeHandler: (combinedInfo: string) =>
        props.handleFileUploadChange?.(
          "proofOfContributionDocumentUrl",
          combinedInfo,
        ),
      required: true,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "vestingValue",
      name: "vestingValue",
      required: !!currentTransition?.vesting_value_required,
      inputType: "number",
      rule: "number",
      label: "Vesting Percentage (0–100)",
      placeholder: "Enter vesting percentage",
      initialValue:
        props.redemptionInfo?.vestingValue !== undefined
          ? Number(props.redemptionInfo.vestingValue)
          : undefined,
      elementStyle: { width: "100%" },
      rules: [
        {
          validator: (_: any, value: number) => {
            if (value === undefined || value === null) return Promise.resolve();
            if (value < 0 || value > 100) {
              return Promise.reject(
                "Vesting percentage must be between 0 and 100",
              );
            }
            return Promise.resolve();
          },
        },
      ],
      colConfig: { span: 24 },
    },
    {
      id: "vestingRuleUrl",
      name: "vestingRuleUrl",
      label: "Vesting Rule Document",
      inputType: "fileUpload",
      placeholder: "Upload vesting rule document",
      rule: "string",
      required: !!currentTransition?.vesting_rule_url_required,
      changeHandler: (combinedInfo: string) =>
        props.handleFileUploadChange?.("vestingRuleUrl", combinedInfo),
      colConfig: { span: 24 },
    },
    {
      id: "notifyCustomer",
      name: "notifyCustomer",
      rule: "boolean",
      inputType: "checkbox",
      required: false,
      label: "Notify Customer?",
      checkboxCheckedValue: props.notifyCustomer ? true : false,
      checkboxChangeHandler: (e) =>
        props.changeNotifyCustomer?.(e.target.checked),
      colConfig: {
        span: 12,
      },
    },
    {
      id: "notifyClient",
      name: "notifyClient",
      rule: "boolean",
      inputType: "checkbox",
      required: false,
      label: "Notify Client?",
      checkboxCheckedValue: props.notifyClient ? true : false,
      checkboxChangeHandler: (e) =>
        props.changeNotifyClient?.(e.target.checked),
      colConfig: {
        span: 12,
      },
    },
  ];
  return (
    <>
      <Spin spinning={props.isLoading}>
        <Row>
          <Col
            span={24}
            style={{
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                marginTop: "10px",
              }}
            >
              <BaseForm
                layout="vertical"
                reference={props.reference}
                fieldsToHide={fieldsToHide}
                fields={fields}
              />
            </div>
          </Col>
        </Row>
      </Spin>
    </>
  );
};

export default class ViewRedemptionRequests extends React.Component<
  Props,
  State
> {
  reference: RefObject<FormInstance>;
  reasonReference: RefObject<FormInstance>;
  followersReference: RefObject<FormInstance>;
  ticketReference: RefObject<FormInstance>;
  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      redemptionRequest: undefined,
      isLoading: true,
      id: "",
      showReasonModal: false,
      showConfirmActionModal: false,
      showTicketForm: false,
      isConfirmActionLoading: false,
      isTicketCreateLoading: false,
      isReasonLoading: false,
      notes: [],
      currentNote: undefined,
      users: [],
      attachments: [],
      attachmentIds: [],
      requestLogs: [],
      showDrawer: false,
      selectedRedemptionNoteId: "",
      redemptionRequestAttachments: [],
      redemptionRequestReasons: [],
      redemptionAllocationAmount: undefined,
      disableReasonButton: false,
      currentRequestsToProcess: 0,
      activeDrawerContent: "",
      showCancelModal: false,
      notifyCustomer: true,
      notifyClient: false,
    };
    this.getRedemptionRequest = this.getRedemptionRequest.bind(this);
    this.reference = React.createRef<FormInstance>();
    this.reasonReference = React.createRef<FormInstance>();
    this.ticketReference = React.createRef<FormInstance>();
    this.followersReference = React.createRef<FormInstance>();
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  setReasonReference = (ref: React.RefObject<FormInstance>): void => {
    this.reasonReference = ref;
  };

  setTicketRef = (ref: React.RefObject<FormInstance>): void => {
    this.ticketReference = ref;
  };

  redirectToAllRedemptionRequestsPage = (): void => {
    this.props.history.push("/redemption-requests");
  };

  getRedemptionRequestLogs = (): void => {
    const { redemptionRequest } = this.state;
    interface Response {
      redemptionRequestChangeLogs: Changelog[];
    }

    this.setState({ isLoading: true });
    request({
      query: GET_REDEMPTION_REQUEST_LOGS,
      variables: { redemptionRequestId: redemptionRequest?.id },
    })
      .then(({ data }: AxiosResponse<{ data: Response }>): void => {
        this.setState({ requestLogs: data?.data?.redemptionRequestChangeLogs });
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  getRedemptionRequest(): void {
    const { id } = this.state;
    interface Response {
      redemptionRequest: RedemptionsRequest;
    }
    const query = GET_REDEMPTION_REQUEST;
    this.setState({ isLoading: true });
    request({
      query,
      variables: { id },
    })
      .then(({ data }: AxiosResponse<{ data: Response }>): void => {
        const { redemptionRequest } = data.data;
        this.setState({
          redemptionRequest,
          availableTransitionStates:
            redemptionRequest?.canTransitionToNextStates,
          notes:
            redemptionRequest?.redemptionRequestNotes?.map(
              (redemptionRequestNote) => {
                return {
                  ...redemptionRequestNote.note,
                  tickets: redemptionRequestNote.redemptionRequestNoteTickets,
                  referenceId: redemptionRequestNote.id,
                };
              },
            ) || [],
          attachments:
            redemptionRequest?.redemptionRequestAttachments?.map(
              (redemptionRequestAttachment) =>
                redemptionRequestAttachment.attachment,
            ) || [],
          redemptionRequestAttachments:
            redemptionRequest?.redemptionRequestAttachments || [],
          redemptionAllocationAmount:
            redemptionRequest?.allocationAmountBreakdown,
          notifyClient: redemptionRequest?.initiator === "company",
        });
        this.getRedemptionRequestLogs();
      })
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  }

  toggleShowReasonModal = (): void => {
    this.setState((previousState: Readonly<State>) => ({
      showReasonModal: !previousState.showReasonModal,
    }));
  };

  toggleConfirmActionModal = (): void => {
    this.setState((previousState: Readonly<State>) => ({
      showConfirmActionModal: !previousState.showConfirmActionModal,
    }));
  };

  saveStateReason = async (): Promise<void> => {
    if (this.state.isReasonLoading) {
      return;
    }

    const { selectedTransitionState, notifyCustomer, notifyClient } =
      this.state;

    let data;

    if (!this.reasonReference.current) return;

    const fieldsToBeValidated = [
      "redemptionRequestReasonId",
      "comment",
      "amountPaid",
      "paidDate",
      "valueDate",
      "vestingValue",
    ];

    try {
      data = await this.reasonReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    interface Response {
      redemptionRequest: RedemptionsRequest;
      errors: APIError[];
    }

    this.setState({ isReasonLoading: true, disableReasonButton: true });
    const variables = {
      redemptionRequestId: this.state.id,
      ...data,
      state: selectedTransitionState,
      notifyCustomer,
      notifyClient,
      vestingRuleUrl: this.state.vestingRuleUrl,
    };
    try {
      const response: AxiosResponse = await request({
        query: MOVE_REDEMPTION_REQUEST_TO_STATE,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "moveRedemptionRequestToState",
      );

      const error = err || mutation.errors;

      if (mutation.redemptionRequest) {
        message.success("Redemption state updated successfully");
        this.getRedemptionRequest();
        this.getRedemptionRequestLogs();
      } else {
        message.error(generateDisplayErrorMessage(error));
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.reasonReference.current.setFieldsValue({ comment: "" });
      this.setState({
        isReasonLoading: false,
        disableReasonButton: false,
        showReasonModal: false,
      });
    }
  };

  requestForReason = async (state: string): Promise<void> => {
    if (this.state.isReasonLoading) {
      return;
    }

    interface Response {
      redemptionRequestReasons: RequestReason[];
      errors: APIError[];
    }

    this.setState({ isReasonLoading: true });

    try {
      request({
        query: GET_REDEMPTION_REQUEST_REASONS,
        variables: { state },
      })
        .then(({ data }: AxiosResponse<{ data: Response }>) => {
          this.setState({
            redemptionRequestReasons: data.data.redemptionRequestReasons,
            showReasonModal: true,
            selectedTransitionState: state,
          });
        })
        .finally(() => {
          this.setState({ isReasonLoading: false });
        });
    } catch (error) {
      message.error(`An error occurred, please try again later - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
      this.setState({ isReasonLoading: false });
    }
  };

  generateButtonColor = (state: string): string => {
    if (["reversed", "cancelled"].includes(state)) return "error-btn";
    if (["paid", "approved", "redemption_approved"].includes(state))
      return "success-btn";
    return "warning-btn";
  };

  componentDidMount(): void {
    this.setState(
      {
        id: this.props.match.params.id,
        currentRequestsToProcess:
          this.props?.location?.state?.requests?.findIndex(
            (item) =>
              item?.id.toString() === this.props.match.params.id.toString(),
          ) ?? 0,
      },
      (): void => {
        this.getRedemptionRequest();
      },
    );
  }

  handleNoteUpdate = (updatedNotes: Note[]) => {
    this.setState({ notes: updatedNotes });
  };

  checkDeals = () => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        checkDeals: {
          result: boolean;
        };
      };
    }

    request({
      query: CHECK_DEALS,
      variables: { id: this.state.id },
    })
      .then(({ data: { data } }: AxiosResponse<Response>): void => {
        if (data.checkDeals.result) {
          message.success("Deal check successfully");
        }
      })
      .catch((error) => {
        message.error("Error checking deals");
      })
      .finally(() => {
        this.setState({
          isLoading: false,
        });
        window.location.reload();
      });
  };

  assignNoteToRedemptionRequest = (noteId: string): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignNoteToRedemptionRequest: {
          redemptionRequestNote: {
            id: string;
            note: Note;
          };
        };
      };
    }

    return request({
      query: ASSIGN_NOTE_TO_REDEMPTION_REQUEST,
      variables: { noteId: noteId, redemptionRequestId: this.state.id },
    }).then(({ data: { data } }: AxiosResponse<Response>): void => {
      let newNote = {
        ...data.assignNoteToRedemptionRequest.redemptionRequestNote.note,
        referenceId:
          data.assignNoteToRedemptionRequest.redemptionRequestNote.id,
      };

      this.setState({
        isLoading: false,
        currentNote: newNote,
        notes: [...this.state.notes, newNote],
      });
    });
  };

  assignAttachmentToRedemptionRequest = async (
    attachmentIds: string[],
    redemptionRequestId: string | number,
  ): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignAttachmentsToRedemptionRequest: {
          redemptionRequest: {
            redemptionRequestAttachments: RedemptionRequestAttachment[];
          };
        };
      };
    }

    return request({
      query: ASSIGN_ATTACHMENT_TO_REDEMPTION_REQUEST,
      variables: {
        attachmentIds,
        redemptionRequestId,
      },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({
        isLoading: false,
        attachmentIds: [],
        redemptionRequestAttachments:
          data.data.assignAttachmentsToRedemptionRequest.redemptionRequest
            .redemptionRequestAttachments,
      });
    });
  };

  createNote = async (): Promise<void> => {
    const { user } = this.props;

    interface Response {
      redemptionRequestNote: Note;
      note: Note;
    }

    const fieldsToBeValidated: string[] = ["description"];

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_NOTE,
        variables: {
          description: data.description,
          userId: user.id,
        },
      });
      this.setState({ isLoading: false });

      const { mutation, err } = responseHandler<Response>(
        response,
        "createNote",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      if (mutation) {
        this.assignNoteToRedemptionRequest(mutation.note.id);
        message.success("Note created successfully");
      }

      this.reference.current &&
        this.reference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ isLoading: false });
    }
  };

  createAttachment = async (combinedInfo: string): Promise<void> => {
    interface Response {
      attachment: Attachment;
    }

    const fieldsToBeValidated: string[] = ["url"];

    this.setState({ isLoading: true });

    try {
      const [url, name] = combinedInfo.split("@@@");
      const response: AxiosResponse = await request({
        query: CREATE_ATTACHMENT,
        variables: {
          url,
          name,
        },
      });
      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "createAttachment",
      );
      this.setState(
        (state) => ({
          attachmentIds: [...state.attachmentIds, mutation.attachment.id],
          attachments: [...state.attachments, mutation.attachment],
        }),
        () =>
          this.assignAttachmentToRedemptionRequest(
            this.state.attachmentIds,
            this.state.id,
          ),
      );

      this.reference.current &&
        this.reference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ isLoading: false });
    }
  };

  unassignAttachmentFromRedemptionRequest = async (
    id: string,
  ): Promise<void> => {
    const { redemptionRequestAttachments } = this.state;

    const redemptionRequestAttachmentToDelete:
      | RedemptionRequestAttachment
      | undefined = redemptionRequestAttachments.find(
      (cra: RedemptionRequestAttachment) => cra.attachment.id === id,
    );

    interface Response {
      result: boolean;
      errors: APIError[];
    }

    try {
      this.setState({ isLoading: true });
      const response: AxiosResponse = await request({
        query: UNASSIGN_ATTACHMENT_FROM_REDEMPTION_REQUEST,
        variables: { id: redemptionRequestAttachmentToDelete?.id },
      });
      const { mutation } = responseHandler<Response>(
        response,
        "unassignAttachmentFromRedemptionRequest",
      );

      if (!mutation.result) {
        message.error("Failed to delete redemption request attachment");
      } else {
        const attachments = this.state.attachments.filter(
          (attachment) => attachment.id !== id,
        );
        this.setState({
          attachments,
          isLoading: false,
        });
        message.success("Deleted Attachment Successfully");
      }
    } catch (error) {
      message.error(`An error occurred, please refresh and try again later - ${generateDisplayErrorMessage(error)}`);
    }
  };

  getAllUsers(): void {
    request({
      query: ALL_USERS_QUERY,
      variables: {
        page: 1,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        users: data.users.results,
        isLoading: false,
      });
    });
  }

  handleRedemptionFollowChange = async (value: string): Promise<void> => {
    const values = value as unknown as string[];

    interface Response {
      redemptionRequest: RedemptionsRequest;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: ASSIGN_FOLLOWERS_TO_REDEMPTIONS_REQUEST,
        variables: {
          redemptionRequestId: this.state.redemptionRequest?.id,
          userIds: values,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "assignFollowersToRedemptionRequest",
      );

      if (err) {
        message.error(`Failed to add follower - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  handleFollowersFocus = () => {
    if (!this.state.users || this.state.users.length === 0) {
      this.getAllUsers();
    }
  };

  getRequestNotifierRecords = (): void => {
    const { id: redemptionRequestId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_REQUEST_NOTIFIER_RECORDS_QUERY,
      variables: { redemptionRequestId },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            redemptionRequest: RedemptionsRequest;
          };
        }>): void => {
          this.setState({
            redemptionRequest: {
              ...this.state.redemptionRequest,
              ...data.data.redemptionRequest,
            },
          });
        },
      )
      .finally((): void => {
        this.setState({
          isLoading: false,
        });
      });
  };

  executeDrawerQuery(currentDrawer: ActiveDrawerContent): void {
    switch (currentDrawer) {
      case "notifierRecords":
        this.getRequestNotifierRecords();
        break;
      case "audits":
        this.getRedemptionRequestLogs();
        break;
      default:
        break;
    }
    return;
  }

  toggleDrawer = (activeDrawerContent: ActiveDrawerContent): (() => void) => {
    return (): void => {
      this.executeDrawerQuery(activeDrawerContent);
      this.setState({
        activeDrawerContent,
        showDrawer: !this.state.showDrawer,
        showCancelModal: false,
      });
    };
  };

  toggleCreateTicket = (id: string): void => {
    this.setState((oldState) => ({
      selectedRedemptionNoteId: id,
      showTicketForm: !oldState.showTicketForm,
      currentNote: this.state.notes.find((note) => note.referenceId === id),
    }));
  };

  createTicket = async (): Promise<void> => {
    const { selectedRedemptionNoteId, redemptionRequest } = this.state;
    const fieldsToBeValidated = [
      "title",
      "description",
      "email",
      "firstName",
      "lastName",
      "source",
      "summary",
      "issue_type",
      "project",
      "mentions",
      "ticketSubCategoryId",
      "type",
      "assigneeId",
    ];
    interface Response {
      ticket: Ticket;
    }

    if (!this.ticketReference.current) return;

    let data;

    try {
      data = await this.ticketReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isTicketCreateLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_NOTE_TICKET,
        variables: {
          redemptionRequestNoteId: selectedRedemptionNoteId,
          properties: {
            ...data,
            first_name: data.firstName,
            last_name: data.lastName,
            system_user: this.props.user.email,
            title: `Redemption Request - ${redemptionRequest?.customer?.firstName} ${redemptionRequest?.customer?.lastName}`,
            group: "Users",
            priority: "2 normal",
            state: "open",
            petra_id: redemptionRequest?.customer?.petraId,
            assigneeId: data.assigneeId,
          },
          source: data.source,
          ticketSubCategoryId: data.ticketSubCategoryId,
        },
      });

      const { mutation, err } = responseHandler<Response>(
        response,
        "createRedemptionRequestNoteTicket",
      );
      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        let currentNote = this.state.currentNote?.tickets
          ? ({
              ...this.state.currentNote,
              tickets: [this.state.currentNote?.tickets, mutation.ticket],
            } as unknown as Note)
          : ({
              ...this.state.currentNote,
              tickets: [mutation.ticket],
            } as unknown as Note);
        this.getRedemptionRequest();
        this.ticketReference.current.resetFields(fieldsToBeValidated);
        this.setState((oldState) => ({
          showTicketForm: !oldState.showTicketForm,
          notes: [...this.state.notes, currentNote],
        }));
        message.success("Ticket Created Successfully");
      }
    } catch (error) {
      message.error(`Could Not Create Ticket - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
    this.setState({ isTicketCreateLoading: false });
  };

  getDrawerContent = (): JSX.Element => {
    const { requestLogs, activeDrawerContent, isLoading } = this.state;

    switch (activeDrawerContent) {
      case "audits":
        return <RequestChangeLogs data={requestLogs} isLoading={isLoading} />;
      case "notifierRecords":
        const records = this.state.redemptionRequest
          ?.notifierRecords as NotificationRecord[];

        return (
          <Fragment>
            {NotificationRecordTableFactory(records, notificationColumns)}
          </Fragment>
        );
      default:
        return <Fragment />;
    }
  };

  getTitleBasedOnDrawerContent = (): React.ReactNode => {
    const { activeDrawerContent } = this.state;
    switch (activeDrawerContent) {
      case "audits":
        return "Redemption Request Logs";
      case "notifierRecords":
        return "Notification Records";
      default:
        return "Entity";
    }
  };

  toggleModal = (): void => {
    this.setState({
      showCancelModal: !this.state.showCancelModal,
    });
  };

  changeNotifyCustomer = (checkedValue: boolean) => {
    this.setState({
      notifyCustomer: checkedValue,
    });
  };

  changeNotifyClient = (checkedValue: boolean) => {
    this.setState({
      notifyClient: checkedValue,
    });
  };

  handleVestingRuleUrlChange = (_field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState({ vestingRuleUrl: url });
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      id,
      redemptionRequest,
      redemptionRequestReasons,
      users,
      redemptionAllocationAmount,
      currentRequestsToProcess,
      showReasonModal,
      selectedTransitionState,
      isReasonLoading,
      showTicketForm,
      isTicketCreateLoading,
      currentNote,
      showDrawer,
      notes,
      attachments,
      disableReasonButton,
      availableTransitionStates,
      notifyCustomer,
      notifyClient,
    } = this.state;
    const reasons: SelectOptionsInterface[] = redemptionRequestReasons?.map(
      (reason) => {
        return { id: reason.id, name: reason.reason.reasonDescription };
      },
    );
    const currentFollowers = redemptionRequest?.redemptionRequestFollowers?.map(
      (follower) => follower.user.id,
    );
    const redemptionRequestFollowers: SelectOptionsInterface[] = users.map(
      (user) => {
        return { id: user.id, name: user.name };
      },
    );

    const noReason: boolean =
      reasons?.length <= 1 &&
      reasons[0]?.name === "Default reason for states without reasons"
        ? true
        : false;

    const moveToNextLink = `/redemption-requests/${
      this.props.location?.state?.requests?.[currentRequestsToProcess + 1]?.id
    }`;

    const moveToPreviousLink = `/redemption-requests/${
      this.props.location?.state?.requests?.[currentRequestsToProcess - 1]?.id
    }`;

    const showCheckDeal = !["cancelled", "paid"].includes(
      redemptionRequest?.state ?? "",
    );

    const nextPreviousUI = (
      <>
        <br />
        <Row
          gutter={10}
          style={{
            float: "right",
            marginTop: "10px",
          }}
        >
          <Col>
            {currentRequestsToProcess > 0 && (
              <NavLink
                to={{
                  pathname: moveToPreviousLink,
                  state: {
                    type: "redemption",
                    requests: this.props?.location?.state?.requests ?? [],
                  },
                }}
              >
                <Tooltip placement="left" title={"Previous"}>
                  <LeftOutlined style={{ color: "#1D3764" }} />
                </Tooltip>
              </NavLink>
            )}
          </Col>

          <Col>
            {this.props?.location?.state?.requests &&
              currentRequestsToProcess <
                this.props?.location?.state?.requests?.length - 1 && (
                <NavLink
                  to={{
                    pathname: moveToNextLink,
                    state: {
                      type: "redemption",
                      requests: this.props?.location?.state?.requests ?? [],
                    },
                  }}
                >
                  <Tooltip placement="right" title={"Next"}>
                    <RightOutlined style={{ color: "#1D3764" }} />
                  </Tooltip>
                </NavLink>
              )}
          </Col>

          <Col>
            {this.props?.location?.state?.requests &&
              `${currentRequestsToProcess + 1} / ${
                this.props?.location?.state?.requests?.length
              }`}
          </Col>
        </Row>
      </>
    );

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            {ModalCreator({
              title: noReason ? "" : "Select Reason",
              okType: "primary",
              okText: noReason ? "Comment" : "Select Reason",
              showModal: showReasonModal,
              okayButtonProps: { disabled: disableReasonButton },
              onOkay: this.saveStateReason,
              toggleModal: this.toggleShowReasonModal,
              isLoading: isReasonLoading,
              inscription: RequestReasonUI({
                reference: this.reasonReference,
                reasons,
                redemptionInfo: redemptionRequest,
                selectedTransitionState,
                availableTransitionStates,
                isLoading: isReasonLoading,
                type: "redemption",
                notifyCustomer,
                notifyClient,
                changeNotifyCustomer: this.changeNotifyCustomer,
                changeNotifyClient: this.changeNotifyClient,
                handleFileUploadChange: this.handleVestingRuleUrlChange,
              }),
            })}
            <Row>
              <Col span={24}>
                {redemptionRequest && (
                  <>
                    <Descriptions
                      title={<div>Details</div>}
                      extra={
                        <div>
                          <Button
                            style={{
                              marginLeft: "5px",
                            }}
                            className="base-button"
                            onClick={this.toggleDrawer("notifierRecords")}
                          >
                            Notifications
                          </Button>
                          <Button
                            style={{
                              marginLeft: "5px",
                            }}
                            className="base-button"
                            onClick={this.toggleDrawer("audits")}
                          >
                            Request History
                          </Button>{" "}
                          {showCheckDeal && (
                            <Button
                              className="base-button"
                              onClick={this.checkDeals}
                            >
                              Check Deals
                            </Button>
                          )}{" "}
                          <NavLink to={`/redemption-requests/${id}/edit`}>
                            <Button
                              icon={<EditOutlined />}
                              style={{}}
                              className="base-button"
                            >
                              Edit
                            </Button>{" "}
                          </NavLink>
                          {nextPreviousUI}
                        </div>
                      }
                    ></Descriptions>
                    <div className="loan-request-details-stack">
                      <AntdCard title="Customer">
                        <Descriptions>
                          <Descriptions.Item
                            label={<b>Requestor Name</b>}
                            key="Customer"
                          >
                            {redemptionRequest.customer ? (
                              <NavLink
                                to={`/customers/${redemptionRequest.customer.id}`}
                              >
                                {generateNameFromCustomer(
                                  redemptionRequest.customer,
                                  false,
                                )}
                              </NavLink>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Petra ID</b>}
                            key="CustomerPetraId"
                          >
                            {redemptionRequest.customer?.petraId || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>ID Type</b>}
                            key="IDType"
                          >
                            {redemptionRequest.idType?.name || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>ID Number</b>}
                            key="IDNumber"
                          >
                            {redemptionRequest.idNumber
                              ? capitalizeFirstLetter(
                                  redemptionRequest.idNumber,
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item label={<b>ID</b>} key="idUrl">
                            {redemptionRequest.idUrl ? (
                              <a
                                target="_blank"
                                href={redemptionRequest.idUrl}
                                rel="noopener noreferrer"
                              >
                                Click To Open Image
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Associated Company</b>}
                            key="Company"
                          >
                            {redemptionRequest.company ? (
                              <NavLink
                                to={`/companies/${redemptionRequest.company.id}`}
                              >
                                {redemptionRequest.company.name}
                              </NavLink>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Client Contact Information</b>}
                            key="clientContactInformation"
                          >
                            {redemptionRequest.clientContactInformation ||
                              "N/A"}
                          </Descriptions.Item>
                        </Descriptions>
                      </AntdCard>
                      <AntdCard
                        title={
                          <span>
                            Redemption Request{" "}
                            {redemptionRequest.state ? (
                              <Tag
                                color={getStateColor(redemptionRequest.state)}
                                style={{
                                  textTransform: "capitalize",
                                  marginLeft: "3px",
                                }}
                              >
                                {formatSnakeCase(redemptionRequest.state)}
                              </Tag>
                            ) : (
                              "N/A"
                            )}
                          </span>
                        }
                      >
                        <Descriptions>
                          <Descriptions.Item
                            label={<b>Batch Number</b>}
                            key="batchNumber"
                          >
                            {redemptionRequest.batchNumber || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Request Type</b>}
                            key="requestType"
                          >
                            {redemptionRequest.subCategory?.name || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Fund Code</b>}
                            key="fundCode"
                          >
                            {redemptionRequest.customerSubscription?.product
                              .fundCode ? (
                              <Text style={{ maxWidth: "90%" }}>
                                {
                                  redemptionRequest.customerSubscription.product
                                    .fundCode
                                }
                              </Text>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Assigned To</b>}
                            key="assignedTo"
                          >
                            {redemptionRequest.user?.name || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Redemption Amount</b>}
                            key="redemptionAmount"
                          >
                            {redemptionRequest.redemptionAmount
                              ? formatter.format(
                                  redemptionRequest.redemptionAmount,
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Initial Redemption Amount</b>}
                            key="initialRedemptionAmount"
                          >
                            {redemptionRequest.initialRedemptionAmount
                              ? formatter.format(
                                  redemptionRequest.initialRedemptionAmount,
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Product</b>}
                            key="product"
                          >
                            {redemptionRequest.customerSubscription?.product
                              .name ? (
                              <Text
                                style={{ maxWidth: "90%" }}
                                ellipsis={{ suffix: "...." }}
                              >
                                {
                                  redemptionRequest.customerSubscription.product
                                    .name
                                }
                              </Text>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Request Reason</b>}
                            key="reason"
                            style={{ textTransform: "capitalize" }}
                          >
                            {redemptionRequest.redemptionRequestReason?.reason
                              .reasonDescription || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Source</b>}
                            key="source"
                            style={{ textTransform: "capitalize" }}
                          >
                            {redemptionRequest.source
                              ? formatSnakeCase(redemptionRequest.source)
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Redemption Type</b>}
                            key="redemptionType"
                          >
                            {redemptionTypes[
                              redemptionRequest.redemptionType
                            ] || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Initiator</b>}
                            key="initiator"
                          >
                            {redemptionRequest.initiator
                              ? capitalizeFirstLetter(
                                  redemptionRequest.initiator,
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Is Former Company</b>}
                            key="IsFormerCompany"
                          >
                            {redemptionRequest.isFormerCompany ? "Yes" : "No"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Purpose</b>}
                            key="Purpose"
                            style={{ textTransform: "capitalize" }}
                          >
                            {redemptionRequest.redemptionRequestPurpose
                              ? formatRedemptionPurpose(
                                  redemptionRequest.redemptionRequestPurpose
                                    .name,
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Other Purpose Description</b>}
                            key="additionalData"
                            style={{ textTransform: "capitalize" }}
                          >
                            {redemptionRequest?.additionalData?.[
                              "additional_purpose_description"
                            ]
                              ? formatRedemptionPurpose(
                                  redemptionRequest.additionalData[
                                    "additional_purpose_description"
                                  ],
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Sub Purpose</b>}
                            key="SubPurpose"
                            style={{ textTransform: "capitalize" }}
                          >
                            {redemptionRequest.redemptionRequestSubPurpose
                              ? formatRedemptionPurpose(
                                  redemptionRequest.redemptionRequestSubPurpose
                                    .redemptionRequestSubPurposeItem?.name,
                                )
                              : "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Redemption Notification Sent To</b>}
                            key="contactInformation"
                          >
                            {redemptionRequest?.contactInformation || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Total Amount Type</b>}
                            key="totalAmountType"
                            style={{ textTransform: "capitalize" }}
                          >
                            {redemptionRequest.totalAmountType || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>TA Transaction Status</b>}
                            key="taTransactionStatus"
                          >
                            {redemptionRequest.taTransactionStatus ? (
                              <Tag
                                color={getStateColor(
                                  redemptionRequest.taTransactionStatus,
                                )}
                                style={{ textTransform: "capitalize" }}
                              >
                                {formatSnakeCase(
                                  redemptionRequest.taTransactionStatus,
                                )}
                              </Tag>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Payment Mode</b>}
                            key="paymentMode"
                            style={{ textTransform: "capitalize" }}
                          >
                            {redemptionRequest.paymentMode
                              ? formatSnakeCase(redemptionRequest.paymentMode)
                              : "N/A"}
                          </Descriptions.Item>
                          
                          {redemptionRequest.unvestedDeal && (
                            <Descriptions.Item
                              label={<b>Unvested Amount</b>}
                              key="unvestedAmount"
                            >
                              {formatter.format(
                                redemptionRequest.unvestedDeal.totalAmount || 0,
                              )}
                            </Descriptions.Item>
                          )}

                          <Descriptions.Item
                            label={<b>Unvested Unassigned</b>}
                            key="unvestedUnassigned"
                          >
                            {redemptionRequest.unvestedUnassigned != null
                              ? formatter.format(
                                  redemptionRequest.unvestedUnassigned,
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Deal Synced?</b>}
                            key="dealProcessed"
                            style={{ textTransform: "capitalize" }}
                          >
                            {redemptionRequest.dealProcessed != null
                              ? redemptionRequest.dealProcessed.toString()
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Risk Percentage</b>}
                            key="riskPercentage"
                          >
                            {redemptionRequest.riskBreakdown ? (
                              <span style={{ color: "red" }}>
                                {
                                  redemptionRequest.riskBreakdown[
                                    "riskPercentage"
                                  ]
                                }
                                %
                              </span>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Vesting Value Type</b>}
                            key="vestingValueType"
                          >
                            {redemptionRequest.vestingValueType
                              ? capitalizeFirstLetter(
                                  redemptionRequest.vestingValueType,
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Vesting Value</b>}
                            key="vestingValue"
                          >
                            {redemptionRequest.vestingValue || "N/A"}
                          </Descriptions.Item>

                          {redemptionRequest.authorizer && (
                            <Descriptions.Item
                              label={<b>Authorizer</b>}
                              key="authorizer"
                            >
                              {redemptionRequest.authorizer.contactName}
                            </Descriptions.Item>
                          )}

                          <Descriptions.Item
                            label={<b>Tax Waiver NPRA</b>}
                            key="taxWaiverNpraUrl"
                          >
                            {redemptionRequest.taxWaiverNpraUrl ? (
                              <a
                                target="_blank"
                                href={redemptionRequest.taxWaiverNpraUrl}
                                rel="noopener noreferrer"
                              >
                                Click To Open Image
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Proof of Vesting Confirmation</b>}
                            key="vestingRuleUrl"
                          >
                            {redemptionRequest.vestingRuleUrl ? (
                              <a
                                target="_blank"
                                href={redemptionRequest.vestingRuleUrl}
                                rel="noopener noreferrer"
                              >
                                Click To Open Document
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Transfer Request</b>}
                            key="TransferRequest"
                          >
                            {redemptionRequest.transferRequest ? (
                              <NavLink
                                to={`/transfer-requests/${redemptionRequest.transferRequest.id}`}
                              >
                                Go To
                              </NavLink>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Amount Paid</b>}
                            key="amountPaid"
                          >
                            {redemptionRequest.amountPaid != null
                              ? formatter.format(redemptionRequest.amountPaid)
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Withholding Tax</b>}
                            key="withholdingTaxAllocation"
                          >
                            {redemptionRequest.withholdingTax != null
                              ? formatter.format(
                                  redemptionRequest.withholdingTax,
                                )
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>External Reference</b>}
                            key="externalReference"
                          >
                            {redemptionRequest.externalReference || "N/A"}
                          </Descriptions.Item>

                          {redemptionRequest.expectsUnvestedDeal && (
                            <Descriptions.Item
                              label={<b>Unvested External Reference</b>}
                              key="unvestedExternalReference"
                            >
                              {redemptionRequest.unvestedExternalReference ||
                                "Pending approval"}
                            </Descriptions.Item>
                          )}
                        </Descriptions>
                      </AntdCard>
                      <AntdCard title="Dates and Timelines">
                        <Descriptions>
                          <Descriptions.Item
                            label={<b>Request Date</b>}
                            key="requestDate"
                          >
                            {redemptionRequest.requestDate
                              ? moment(
                                  redemptionRequest.requestDate,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("llll")
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Correct Info Date</b>}
                            key="correctInfoDate"
                          >
                            {redemptionRequest?.correctInfoDate
                              ? moment(
                                  redemptionRequest.correctInfoDate,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("llll")
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Paid Date</b>}
                            key="paidDate"
                          >
                            {redemptionRequest?.paidDate
                              ? moment(
                                  redemptionRequest.paidDate,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("DD/MM/YYYY")
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Created At</b>}
                            key="createdAt"
                          >
                            {redemptionRequest.createdAt
                              ? moment(
                                  redemptionRequest.createdAt,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("llll")
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Due Date</b>}
                            key="dueDate"
                          >
                            {redemptionRequest.dueDate
                              ? moment(
                                  redemptionRequest.dueDate,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("llll")
                              : "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Value Date</b>}
                            key="valueDate"
                          >
                            {redemptionRequest?.valueDate
                              ? moment(
                                  redemptionRequest.valueDate,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("MMM DD, YYYY")
                              : "N/A"}
                          </Descriptions.Item>
                        </Descriptions>
                      </AntdCard>
                    </div>
                  </>
                )}
              </Col>
            </Row>

            {redemptionRequest?.state !== "cancelled" && (
              <div
                style={{
                  marginTop: "25px",
                  display: "flex",
                  justifyContent: "start",
                }}
              >
                {availableTransitionStates?.map((transition, i) => (
                  <Button
                    onClick={() => this.requestForReason(transition.state)}
                    className={this.generateButtonColor(transition.state)}
                    id={`transition-btn-${i}`}
                    key={i}
                    style={{ marginRight: "5px" }}
                    disabled={isReasonLoading}
                  >
                    {generateStateButtonText(transition.event)}
                  </Button>
                ))}
              </div>
            )}
          </Fragment>
        </Card>

        <Collapse style={{ marginTop: "50px", marginBottom: "50px" }}>
          <Panel
            header={
              <span>
                Redemption Allocation Breakdown{" "}
                <Badge
                  count={redemptionAllocationAmount ? 1 : 0}
                  style={tabPaneStyle}
                />
              </span>
            }
            key={"RedemptionAllocation"}
          >
            {redemptionAllocationAmount && (
              <RedemptionAllocationBreakdown
                allocation={redemptionAllocationAmount}
                redemptionRequest={redemptionRequest}
              />
            )}
          </Panel>

          {redemptionRequest?.deal && (
            <Panel
              header={
                <span>
                  Deal Information <Badge count={1} style={tabPaneStyle} />
                </span>
              }
              key="dealInfo"
            >
              <DealInfoContent
                deal={redemptionRequest.deal}
                title="Deal Information"
              />
            </Panel>
          )}

          {redemptionRequest?.unvestedDeal && (
            <Panel
              header={
                <span>
                  Unvested Deal Information{" "}
                  <Badge count={1} style={tabPaneStyle} />
                </span>
              }
              key="unvestedDealInfo"
            >
              <DealInfoContent
                deal={redemptionRequest.unvestedDeal}
                title="Unvested Deal Information"
              />
            </Panel>
          )}

          <Panel
            header={
              <span>
                Redemption Payout Account{" "}
                <Badge
                  count={redemptionRequest?.payoutAccount ? 1 : 0}
                  style={tabPaneStyle}
                />
              </span>
            }
            key={"RedemptionPayoutAccount"}
          >
            {redemptionRequest?.payoutAccount && (
              <Col span={8} style={{ marginTop: "20px" }}>
                <AntdCard
                  title={<b>{"Payout Account"}</b>}
                  size="small"
                  style={{
                    width: 300,
                    borderTop: "4px solid rgb(29, 55, 100)",
                    borderRadius: "4px",
                  }}
                >
                  <PaymentAccountCard
                    paymentAccount={redemptionRequest?.payoutAccount}
                  />
                </AntdCard>
              </Col>
            )}
          </Panel>

          <Panel
            header={
              <span>
                Risk Breakdown{" "}
                <Badge
                  count={
                    redemptionRequest?.riskBreakdown?.riskCounts?.length || 0
                  }
                  style={tabPaneStyle}
                />
              </span>
            }
            key="RiskBreakdown"
          >
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              {redemptionRequest?.riskBreakdown?.riskCounts?.map(
                (riskGroup: any) => {
                  const groupedRisks =
                    redemptionRequest?.riskBreakdown.risks.filter(
                      (risk: any) => risk.riskName === riskGroup.riskName,
                    );

                  return (
                    <Col
                      key={riskGroup.riskName}
                      xs={24}
                      sm={24}
                      md={12}
                      lg={8}
                      xl={8}
                    >
                      <AntdCard
                        title={
                          <div>
                            <b>{riskGroup.riskName}</b>{" "}
                            <Badge
                              count={riskGroup.count}
                              style={tabPaneStyle}
                            />
                          </div>
                        }
                        size="small"
                        style={{
                          borderTop: "4px solid rgb(29, 55, 100)",
                          borderRadius: "4px",
                          width: "100%",
                        }}
                      >
                        {groupedRisks.map((risk: any) => (
                          <div
                            key={risk.id}
                            style={{
                              padding: "8px 0",
                              borderBottom: "1px solid #f0f0f0",
                              fontSize: "14px",
                            }}
                          >
                            <div>
                              <b>Customer:</b> {risk.customer}
                            </div>
                            {risk.paymentId && risk.paymentId !== "0" ? (
                              <div>
                                <b>Payment ID:</b> {risk.paymentId}
                              </div>
                            ) : (
                              risk.redemptionExternalReference !==
                                "00000000-0000-0000-0000-000000000000" && (
                                <div>
                                  <b>Redemption External Reference:</b>{" "}
                                  {risk.redemptionExternalReference}
                                </div>
                              )
                            )}
                            <div>
                              <b>Created At:</b>{" "}
                              {moment(risk?.createdAt, "YYYY-MM-DD").format(
                                "llll",
                              )}
                            </div>
                            <div>
                              <b>Created By:</b> {risk.createdBy}
                            </div>
                            <div>
                              <b>Risk Point:</b> {risk.riskPoint}
                            </div>
                          </div>
                        ))}
                      </AntdCard>
                    </Col>
                  );
                },
              )}
            </Row>
          </Panel>

          <Panel header="File Path" key={"FilePath"}>
            {redemptionRequest?.additionalData && (
              <FilePath redemptionRequest={redemptionRequest} />
            )}
          </Panel>

          <Panel
            header={
              <span>
                Attachments{" "}
                <Badge
                  count={
                    redemptionRequest?.redemptionRequestAttachments?.length || 0
                  }
                  style={tabPaneStyle}
                />
              </span>
            }
            key={"Attachments"}
          >
            <AttachmentsSection
              reference={this.reference}
              createAttachment={this.createAttachment}
              attachments={attachments}
              showForm={true}
              handleRemove={this.unassignAttachmentFromRedemptionRequest}
            />
          </Panel>
          <Panel
            header={
              <span>
                Notes{" "}
                <Badge
                  count={redemptionRequest?.redemptionRequestNotes?.length || 0}
                  style={tabPaneStyle}
                />
              </span>
            }
            key={"Notes"}
          >
            {redemptionRequest && (
              <NotesSection
                showHeading={true}
                reference={this.reference}
                createNote={this.createNote}
                notes={notes}
                showForm={true}
                canCreateTicket={true}
                showTicketForm={showTicketForm}
                toggleTicketCreate={this.toggleCreateTicket}
                createTicket={this.createTicket}
                ticketLoading={isTicketCreateLoading}
                ticketReference={this.ticketReference}
                customer={redemptionRequest.customer}
                currentNote={currentNote}
                onNoteUpdate={this.handleNoteUpdate}
                entity="RedemptionRequest"
              />
            )}
          </Panel>

          <Panel header="Followers" key={"Followers"}>
            {redemptionRequest && (
              <BaseForm
                style={{
                  marginTop: "20px",
                }}
                layout="vertical"
                reference={this.followersReference}
                fields={[
                  {
                    isArrayField: true,
                    id: "followers",
                    name: "followers",
                    inputType: "multipleSelect",
                    label: "Select Followers",
                    rule: "string",
                    placeholder: "Select Followers",
                    required: false,
                    initialValue: currentFollowers,
                    changeHandler: this.handleRedemptionFollowChange,
                    colConfig: {
                      span: 12,
                    },
                    selectOptions: redemptionRequestFollowers,
                    onFocus: this.handleFollowersFocus,
                  },
                ]}
                fieldsToHide={[]}
              />
            )}
          </Panel>

          {redemptionRequest && redemptionRequest.companySubscription && (
            <Panel header="Subscriptions" key={"Subscriptions"}>
              <Divider className="view-company-ant-divider" />
              <Tabs type="card">
                <Tabs.TabPane tab="Subscriptions">
                  <Descriptions.Item
                    label={<b>Subscriptions</b>}
                    key="Trustee Scheme"
                  >
                    {redemptionRequest.companySubscription?.product && (
                      <CardSection
                        headingText="Product"
                        data={
                          redemptionRequest.companySubscription?.product.name
                        }
                      />
                    )}
                  </Descriptions.Item>
                </Tabs.TabPane>
              </Tabs>
            </Panel>
          )}
        </Collapse>

        <Drawer
          title={this.getTitleBasedOnDrawerContent()}
          width={1200}
          onClose={this.toggleModal}
          visible={showDrawer}
          bodyStyle={{ paddingBottom: 80 }}
          maskClosable={false}
        >
          {this.getDrawerContent()}
        </Drawer>
        {this.state.showCancelModal &&
          ModalCreator({
            title: "Cancel",
            okType: "primary",
            okText: "Yes",
            width: 400,
            showModal: true,
            onOkay: this.toggleDrawer(""),
            toggleModal: this.toggleModal,
            showFooter: true,
            inscription: `Are you sure you want to cancel ${spaceCamel(
              this.state.activeDrawerContent,
            ).toLocaleLowerCase()} ?`,
          })}
      </Spin>
    );
  }
}
